import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { MODULE_TYPES } from "@/admin/schemas";

const NEEDS_OPTIONS = ["COMMUNITY_FRAGE", "WEGWAHL"];
const NEEDS_CODES = ["CODE_SUCHE", "FUNDSTUECK"];

export function ModulesEditor({ value, onChange, discoveryItems }) {
  const modules = Array.isArray(value) ? value : [];
  const [type, setType] = useState("INFO");

  const update = (index, patch) => onChange(modules.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  const add = () =>
    onChange([...modules, { key: `m${Date.now().toString(36)}`, type, title: "", text: "", options: [], codes: [] }]);

  return (
    <div data-testid="modules-editor">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className="field !w-auto" value={type} onChange={(e) => setType(e.target.value)} data-testid="module-type-select">
          {MODULE_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button type="button" className="btn btn-ghost !py-2 !text-xs" onClick={add} data-testid="module-add">
          <Plus className="h-4 w-4" /> Modul hinzufügen
        </button>
      </div>

      <div className="space-y-3">
        {modules.length === 0 ? <p className="text-sm text-[var(--ink-soft)]">Noch keine Module.</p> : null}
        {modules.map((module, index) => (
          <div key={module.key || index} className="rounded-xl border bg-[var(--paper)] p-4" data-testid={`module-item-${index}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-bold tracking-wide">{module.type}</span>
              <div className="flex gap-1">
                <button type="button" className="btn btn-ghost !px-2 !py-1 !text-xs" onClick={() => onChange(modules.filter((_, i) => i !== index))} data-testid={`module-remove-${index}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-3">
              <input className="field" placeholder="Titel" value={module.title || ""} onChange={(e) => update(index, { title: e.target.value })} data-testid={`module-title-${index}`} />
              <textarea className="field min-h-[70px]" placeholder="Text" value={module.text || ""} onChange={(e) => update(index, { text: e.target.value })} data-testid={`module-text-${index}`} />
              {module.type === "ENTDECKUNGSFENSTER" ? (
                <select className="field" value={module.discovery_item_id || ""} onChange={(e) => update(index, { discovery_item_id: e.target.value || null })} data-testid={`module-discovery-${index}`}>
                  <option value="">— Entdeckungsinhalt wählen —</option>
                  {(discoveryItems || []).map((item) => (
                    <option key={item.id} value={item.id}>{item.title}</option>
                  ))}
                </select>
              ) : null}
              {NEEDS_CODES.includes(module.type) ? (
                <div>
                  <p className="eyebrow mb-1">Codes</p>
                  {(module.codes || []).map((code, ci) => (
                    <div key={ci} className="mb-2 flex gap-2">
                      <input
                        className="field"
                        placeholder="Code"
                        value={code.code || ""}
                        onChange={(e) => update(index, { codes: module.codes.map((c, i) => (i === ci ? { ...c, code: e.target.value } : c)) })}
                        data-testid={`module-code-${index}-${ci}`}
                      />
                      <input
                        className="field"
                        placeholder="Bezeichnung"
                        value={code.label || ""}
                        onChange={(e) => update(index, { codes: module.codes.map((c, i) => (i === ci ? { ...c, label: e.target.value } : c)) })}
                      />
                      <button type="button" className="btn btn-ghost !px-3 !py-1" onClick={() => update(index, { codes: module.codes.filter((_, i) => i !== ci) })}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-ghost !py-1.5 !text-xs" onClick={() => update(index, { codes: [...(module.codes || []), { code: "", label: "" }] })} data-testid={`module-code-add-${index}`}>
                    Code hinzufügen
                  </button>
                  <input
                    className="field mt-2"
                    placeholder="Text nach dem Finden (optional)"
                    value={module.after_text || ""}
                    onChange={(e) => update(index, { after_text: e.target.value })}
                  />
                </div>
              ) : null}
              {NEEDS_OPTIONS.includes(module.type) ? (
                <div>
                  <p className="eyebrow mb-1">Antwortoptionen</p>
                  {(module.options || []).map((option, oi) => (
                    <div key={oi} className="mb-2 flex gap-2">
                      <input
                        className="field"
                        placeholder="Label"
                        value={option.label || ""}
                        onChange={(e) => update(index, { options: module.options.map((o, i) => (i === oi ? { ...o, label: e.target.value } : o)) })}
                        data-testid={`module-option-${index}-${oi}`}
                      />
                      {module.type === "WEGWAHL" ? (
                        <>
                          <input
                            className="field"
                            placeholder="Varianten-Schlüssel"
                            value={option.variant_key || ""}
                            onChange={(e) => update(index, { options: module.options.map((o, i) => (i === oi ? { ...o, variant_key: e.target.value } : o)) })}
                          />
                          <input
                            className="field"
                            placeholder="Beschreibung"
                            value={option.description || ""}
                            onChange={(e) => update(index, { options: module.options.map((o, i) => (i === oi ? { ...o, description: e.target.value } : o)) })}
                          />
                        </>
                      ) : null}
                      <button type="button" className="btn btn-ghost !px-3 !py-1" onClick={() => update(index, { options: module.options.filter((_, i) => i !== oi) })}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-ghost !py-1.5 !text-xs" onClick={() => update(index, { options: [...(module.options || []), { label: "" }] })} data-testid={`module-option-add-${index}`}>
                    Option hinzufügen
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ModulesEditor;
